/*
 * Description: Display Table Web Component
 * Purpose: Displays a well-formatted Bootstrap table from JSON attributes or slot content
 * Author: Nuvemx
 * Created: 2026
 *
 * Features:
 * - JSON-driven table from columns and rows attributes
 * - Slot-based table content
 * - Bootstrap table modifiers: striped, hover, bordered, sm
 *
 * Usage:
 * <display-table
 *   columns='[{"key":"name","label":"Name"},{"key":"age","label":"Age"}]'
 *   rows='[{"name":"Alice","age":30},{"name":"Bob","age":25}]'
 *   striped hover
 * ></display-table>
 *
 * <display-table bordered>
 *   <table class="table"><thead>...</thead><tbody>...</tbody></table>
 * </display-table>
 *
 * Attributes:
 * - columns: String (JSON) - Array of { key, label } objects
 * - rows: String (JSON) - Array of row objects
 * - striped: Boolean - Bootstrap striped rows
 * - hover: Boolean - Bootstrap hover effect
 * - bordered: Boolean - Bootstrap bordered table
 * - sm: Boolean - Bootstrap small table
 *
 * TODO:
 * - Add sorting support
 * - Add pagination
 */

import './display_table.css';

class display_table extends HTMLElement {
    connectedCallback() {
        if (this.dataset.rendered) {
            return;
        }
        const columnsRaw = this.getAttribute('columns');
        const rowsRaw = this.getAttribute('rows');
        const striped = this.hasAttribute('striped');
        const hover = this.hasAttribute('hover');
        const bordered = this.hasAttribute('bordered');
        const sm = this.hasAttribute('sm');

        const classes = ['table'];
        if (striped) classes.push('table-striped');
        if (hover) classes.push('table-hover');
        if (bordered) classes.push('table-bordered');
        if (sm) classes.push('table-sm');

        if (columnsRaw && rowsRaw) {
            this._renderFromAttributes(columnsRaw, rowsRaw, classes);
        } else {
            this._renderFromSlot(classes);
        }
        this.dataset.rendered = "true";
    }

    _renderFromAttributes(columnsRaw, rowsRaw, classes) {
        let columns, rows;
        try {
            columns = JSON.parse(columnsRaw);
            rows = JSON.parse(rowsRaw);
        } catch (e) {
            this.innerHTML = `
                <div class="alert alert-danger">
                    Invalid JSON in columns or rows attribute.
                </div>`;
            return;
        }

        const headers = columns.map(col =>
            `<th>${col.label || col.key}</th>`
        ).join('');

        const body = rows.map(row =>
            `<tr>${columns.map(col =>
                `<td>${row[col.key] !== undefined ? row[col.key] : ''}</td>`
            ).join('')}</tr>`
        ).join('');

        this.innerHTML = `
            <div class="table-responsive">
                <table class="${classes.join(' ')} table-component">
                    <thead class="table-component-head">
                        <tr>${headers}</tr>
                    </thead>
                    <tbody>${body}</tbody>
                </table>
            </div>`;
    }

    _renderFromSlot(classes) {
        const content = this.innerHTML;
        this.innerHTML = `
            <div class="table-responsive">
                <div class="${classes.join(' ')}-wrapper table-component">
                    ${content}
                </div>
            </div>`;
    }
}

export { display_table };
